import {
  Box,
  CloseButton,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Image,
  Input,
  Progress,
  Select,
  SimpleGrid,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { Controller } from "react-hook-form";
import ReactSelect from "react-select";
import { useDropdownOptions } from "../../../hooks/useDropdownOptions";

/* ------------------ Styles ------------------ */

const inputLikeSelect = {
  borderRadius: "6px",
  minH: "38px",
  borderColor: "gray.300",
  _hover: { borderColor: "gray.400" },
  _focus: {
    borderColor: "blue.500",
    boxShadow: "0 0 0 1px #2684FF",
  },
};

const reactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    borderRadius: "6px",
    borderColor: "#CBD5E0",
    boxShadow: "none",
    ":hover": { borderColor: "#A0AEC0" },
  }),
};

const ItemFormStep = ({
  register,
  control,
  errors,
  type,
  cardImg,
  setValue,
  getValues,
  isUploading,
  uploadedFile,
  handleFileUpload,
  onSubmit,
}) => {
  const { options: authGroupOptions } = useDropdownOptions("AUTHGROUPS");

  return (
    <form id="item-form" onSubmit={onSubmit}>
      <SimpleGrid columns={2} spacing={4}>
        {/* Title */}
        <FormControl isRequired isInvalid={!!errors.title}>
          <FormLabel>Title</FormLabel>
          <Input
            {...register("title", { required: "Title is required" })}
            sx={inputLikeSelect}
          />
          <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
        </FormControl>

        {/* Subtitle */}
        <FormControl>
          <FormLabel>Subtitle</FormLabel>
          <Input {...register("subTitle")} sx={inputLikeSelect} />
        </FormControl>

        {/* Description */}
        <FormControl gridColumn="span 2">
          <FormLabel>Description</FormLabel>
          <Textarea {...register("desc")} rows={3} sx={inputLikeSelect} />
        </FormControl>

        {/* Type */}
        <FormControl isRequired>
          <FormLabel>Type</FormLabel>
          <Select {...register("type")} sx={inputLikeSelect}>
            <option value="file">File</option>
            <option value="powerbi">Power BI</option>
            <option value="tableau">Tableau</option>
            <option value="directory">Directory</option>
            <option value="link">Link</option>
            <option value="Static">Static</option>
          </Select>
        </FormControl>

        {/* Auth Group */}
        <FormControl>
          <FormLabel>Auth Group</FormLabel>
          <Controller
            control={control}
            name="authGroup"
            render={({ field }) => (
              <ReactSelect
                {...field}
                options={authGroupOptions}
                styles={reactSelectStyles}
                value={authGroupOptions.find((o) => o.value === field.value)}
                onChange={(opt) => field.onChange(opt.value)}
              />
            )}
          />
        </FormControl>

        {/* Card Image */}
        <FormControl gridColumn="span 2">
          <FormLabel>Card Image URL</FormLabel>
          <Input {...register("cardImg")} sx={inputLikeSelect} />
          <FormHelperText>keyword (pbi / tableau)</FormHelperText>

          {cardImg && cardImg.startsWith("http") && (
            <Box
              mt={3}
              position="relative"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="6px"
              p={2}
              maxW="220px"
            >
              <CloseButton
                size="sm"
                position="absolute"
                top="4px"
                right="4px"
                bg="white"
                onClick={() => setValue("cardImg", "")}
              />

              <Image
                src={cardImg}
                alt="Preview"
                maxH="120px"
                objectFit="contain"
                borderRadius="4px"
              />
            </Box>
          )}
        </FormControl>

        {/* Link / Static */}
        {(type === "link" || type === "Static") && (
          <FormControl gridColumn="span 2" isInvalid={!!errors.link}>
            <FormLabel>URL</FormLabel>
            <Input {...register("link")} sx={inputLikeSelect} />
            <FormErrorMessage>{errors.link?.message}</FormErrorMessage>
          </FormControl>
        )}

        {/* Power BI */}
        {type === "powerbi" && (
          <>
            <FormControl gridColumn="span 2" isInvalid={!!errors.embedURL}>
              <FormLabel>Embed URL</FormLabel>
              <Input
                {...register("embedURL", {
                  validate: () => {
                    const { embedURL, workspaceId, reportId } = getValues();

                    if (embedURL) return true;
                    if (workspaceId && reportId) return true;

                    return "Embed URL or Workspace + Report ID required";
                  },
                })}
                sx={inputLikeSelect}
              />
              <FormErrorMessage>{errors.embedURL?.message}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Workspace ID</FormLabel>
              <Input {...register("workspaceId")} sx={inputLikeSelect} />
            </FormControl>

            <FormControl>
              <FormLabel>Report ID</FormLabel>
              <Input {...register("reportId")} sx={inputLikeSelect} />
            </FormControl>
          </>
        )}

        {/* Tableau */}
        {type === "tableau" && (
          <FormControl
            gridColumn="span 2"
            isRequired
            isInvalid={!!errors.embedd}
          >
            <FormLabel>Tableau Embed URL</FormLabel>
            <Input
              {...register("embedd", {
                required: "Tableau Embed URL is required",
              })}
              sx={inputLikeSelect}
            />
            <FormErrorMessage>{errors.embedd?.message}</FormErrorMessage>
          </FormControl>
        )}

        {/* Directory - Container field only */}
        {type === "directory" && (
          <FormControl
            gridColumn="span 2"
            isRequired
            isInvalid={!!errors.container}
          >
            <FormLabel>Container</FormLabel>
            <Input
              {...register("container", {
                required: "Container is required",
              })}
              sx={inputLikeSelect}
            />
            <FormErrorMessage>{errors.container?.message}</FormErrorMessage>
          </FormControl>
        )}

        {/* File Upload */}
        {type === "file" && (
          <>
            {/* File Upload Input */}
            <FormControl gridColumn="span 2" isRequired>
              <FormLabel>Upload File</FormLabel>
              <Input
                type="file"
                onChange={handleFileUpload}
                disabled={isUploading}
                sx={{
                  ...inputLikeSelect,
                  pt: "6px",
                }}
              />
              <FormHelperText>
                Upload a file to auto-populate blob name and container
              </FormHelperText>

              {/* Upload Progress */}
              {isUploading && (
                <Box mt={2}>
                  <Progress size="sm" isIndeterminate colorScheme="teal" />
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Uploading file...
                  </Text>
                </Box>
              )}

              {/* Upload Success Message */}
              {uploadedFile && !isUploading && (
                <Text fontSize="sm" color="green.600" mt={2}>
                  ✓ File uploaded: {uploadedFile.name}
                </Text>
              )}
            </FormControl>

            {/* Blob Name (auto-populated) */}
            <FormControl isRequired isInvalid={!!errors.blobName}>
              <FormLabel>Blob Name</FormLabel>
              <Input
                {...register("blobName", {
                  required: "Blob name is required",
                })}
                sx={inputLikeSelect}
                readOnly
                bg="gray.50"
              />
              <FormErrorMessage>{errors.blobName?.message}</FormErrorMessage>
            </FormControl>

            {/* Container (auto-populated) */}
            <FormControl isRequired isInvalid={!!errors.container}>
              <FormLabel>Container</FormLabel>
              <Input
                {...register("container", {
                  required: "Container is required",
                })}
                sx={inputLikeSelect}
                readOnly
                bg="gray.50"
              />
              <FormErrorMessage>{errors.container?.message}</FormErrorMessage>
            </FormControl>
          </>
        )}
      </SimpleGrid>
    </form>
  );
};

export default ItemFormStep;
